import { DestroyRef, inject, Injectable, Signal, signal, computed, effect, untracked } from '@angular/core';

import {
  WigetNewsGQL,
  News,
  SetIsReadGQL,
  IdOrKeyInput,
} from './graphql/news.sitos-x-api.g';

import { DataService, DataError, type PollStart } from './data.service';
import { catchError, map, take, throwError, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {

  #debug: boolean = false;
  private readonly destroyRef = inject(DestroyRef);

  // Dependency Injection
  private readonly wigetNewsGQL = inject(WigetNewsGQL);
  private readonly setIsReadGQL = inject(SetIsReadGQL);

  // Internal data service
  private readonly dataService!: DataService<News>;

  // Verzögerung fuer den ersten Reload nach einem erfolgreichen Write. Gibt dem
  // (nachhinkenden) Backend-Read-Modell Zeit, den Wert zu uebernehmen, bevor neu
  // geladen wird -> kein sofortiger Fetch, der noch stale isRead-Werte zurueckspielt.
  // Das eigentliche Poll-Intervall liegt zentral in der DataService-Config (Default 60000).
  readonly #postWriteReloadDelayMs = 3000;

  // TEST-Schalter: optimistisches Read-Overlay (#pendingReads) an/aus.
  //   true  -> optimistische Zwischenspeicherung aktiv (siehe #pendingReads).
  //   false -> es werden AUSSCHLIESSLICH tatsächlich geladene DB-Daten angezeigt;
  //            kein Overlay, kein Reconcile, kein Rollback. Zum Wiederaufnehmen
  //            einfach auf true setzen – die gesamte Overlay-Logik bleibt erhalten.
  readonly #optimisticReadsEnabled = true;

  // Read-Zustände, die wir ÜBER den Server-Stand legen: optimistisch gesetzt und –
  // nach erfolgreicher Persistierung – gehalten, bis das (ggf. nachhinkende)
  // Backend-Read-Modell denselben Wert liefert. Verhindert das Zurückspringen des
  // gerade gelesenen Items nach einem sofortigen Reload.
  readonly #pendingReads = signal<ReadonlyMap<string, boolean>>(new Map());

  // Öffentliches Signal: Server-Stand (dataService.data) mit übergelegten pending Reads.
  // Reagiert synchron -> Liste UND unreadNotificationsCount aktualisieren sich sofort.
  readonly notifications: Signal<News[]> = computed<News[]>(() => {
    const data = this.dataService.data();
    // TEST: Overlay deaktiviert -> nur echte DB-Daten anzeigen.
    if (!this.#optimisticReadsEnabled) return data;
    const pending = this.#pendingReads();
    if (pending.size === 0) return data;
    return data.map(n =>
      n.userNewsUuid && pending.has(n.userNewsUuid)
        ? { ...n, isRead: pending.get(n.userNewsUuid)! }
        : n,
    );
  });

  constructor() {
    this.dataService = new DataService<News>(
      {
        name: 'NotificationsService', // Log-Praefix, siehe DataServiceConfig.name
        // Live-Feed: IMMER frisch aus der DB. Kein Cache auf DataService-Ebene und
        // (siehe #fetchNotifications) fetchPolicy 'no-cache' auf Apollo-Ebene.
        // cacheDuration/enableBackgroundRefresh greifen ausschliesslich im (hier nie
        // genutzten) Cache-Zweig von load() -> bewusst weggelassen bzw. false.
        cacheEnabled: false,
        requestTimeout: 15000, // 15s – passt zum 60s-Poll (Worst Case inkl. Retries < 60s)
        maxRetries: 2,
        enableBackgroundRefresh: false,
        // Notifications melden Ladefehler bewusst als Toast (Consumer: navigation-menu).
        showErrorMessages: true,
      },
      this.destroyRef
    );

    // Reconcile: sobald der Server-Stand einen pending Read-Wert bestätigt, wird das
    // Overlay für dieses Item entfernt. Liest NUR dataService.data (untracked auf
    // #pendingReads) -> das Schreiben von #pendingReads triggert diesen Effect nicht erneut.
    effect(() => {
      const data = this.dataService.data();
      // TEST: Overlay deaktiviert -> kein Reconcile nötig.
      if (!this.#optimisticReadsEnabled) return;
      untracked(() => {
        const pending = this.#pendingReads();
        if (pending.size === 0) return;
        const next = new Map(pending);
        let changed = false;
        for (const n of data) {
          const uuid = n.userNewsUuid;
          if (uuid && next.has(uuid) && n.isRead === next.get(uuid)) {
            next.delete(uuid);
            changed = true;
          }
        }
        if (changed) this.#pendingReads.set(next);
      });
    });
  }

  // Public signals from data service (via getters)
  get loading(): Signal<boolean> {
    return this.dataService.loading;
  }

  get error(): Signal<DataError | null> {
    return this.dataService.error;
  }

  get lastUpdated(): Signal<number | null> {
    return this.dataService.lastUpdated;
  }

  get isCacheValid(): Signal<boolean> {
    return this.dataService.isCacheValid;
  }

  /**
   * Ob Ladefehler als benutzersichtbarer Toast gemeldet werden sollen. Der Consumer
   * (navigation-menu) gated seine Toast-Anzeige an diesem Flag. Konfiguriert ueber
   * die DataService-Config (hier: true).
   */
  get showErrorMessages(): boolean {
    return this.dataService.showErrorMessages;
  }

  /**
   * Load all notifications (current only load news)
   * 
   * Returns cached data immediately if available, refreshes in background.
   * 
   * @param forceRefresh - Force cache refresh
   */
  loadNotifications(
    forceRefresh: boolean = false
  ): void {
    const fetchFn = () => this.#fetchNotifications();
    this.dataService.load(fetchFn, forceRefresh);
  }

  /**
   * Refresh notifications (force reload)
   */
  refresh(
  ): void {
    this.dataService.forceRefresh(() => this.#fetchNotifications());
  }

  /**
   * Clear notifications cache and state
   */
  clearCache(): void {
    this.dataService.clearCache();
  }

  /**
   * Startet das 60s-Polling (delegiert an DataService, Pattern B). Das Intervall kommt
   * aus der DataService-Config (Default 60000); startPolling ruft intern stopPolling.
   * @param start  Startzeitpunkt des ersten Ticks. 0 (Default): sofortiger frischer
   *   Reload aus der DB. n ms: erster Reload erst nach dieser Zeit – z. B. nach einem
   *   Write, damit das nachhinkende Backend-Read-Modell nachziehen kann. 'interval':
   *   erst mit dem naechsten regulaeren Tick (s. PollStart).
   */
  startPolling(start: PollStart = 0): void {
    this.dataService.startPolling(() => this.#fetchNotifications(), start);
  }

  /**
   * Stoppt das Polling (z. B. während einer Read-Persistierung, um das Race
   * zwischen einem Poll-Load und dem noch nicht persistierten Write zu vermeiden).
   */
  stopPolling(): void {
    this.dataService.stopPolling();
  }

  /**
   * Persistiert den Read-Status robust und stellt zeitnah den aktuellsten Stand her:
   *  1. Overlay optimistisch setzen  -> Liste + unreadNotificationsCount aktualisieren
   *     sich SOFORT (kein Netzwerk, kein Flackern).
   *  2. Polling stoppen              -> kein Race zwischen Poll und Write.
   *  3. Mutation ausführen.
   *     - Erfolg: Overlay HALTEN, Polling neu starten. Der sofortige Poll-Tick lädt
   *       frisch aus der DB (no-cache); das Overlay überbrückt ein evtl. nachhinkendes
   *       Read-Modell, bis der Server denselben Wert liefert (Reconcile-Effect räumt auf).
   *     - Fehler: Overlay entfernen (Rollback), Polling neu starten.
   */
  markNotificationRead(userNewsUuid: string, isRead: boolean = true): Observable<unknown> {

    if(this.#debug) console.log('[NotificationsService] markNotificationRead, userNewsUuid=', userNewsUuid, ', isRead=', isRead);

    if (this.#optimisticReadsEnabled) this.#setPendingRead(userNewsUuid, isRead);
    this.stopPolling();

    return this.setIsRead(userNewsUuid, isRead).pipe(
      tap({
        // Erst nach #postWriteReloadDelayMs frisch laden – Backend-Read-Modell darf
        // nachziehen; bis dahin ueberbrueckt das Overlay (#pendingReads) den Wert
        // (sofern aktiviert).
        next: () => this.startPolling(this.#postWriteReloadDelayMs),
        error: () => {
          if (this.#optimisticReadsEnabled) this.#clearPendingRead(userNewsUuid); // Rollback der optimistischen Änderung
          this.startPolling();                  // sofortiger Reload: echter Server-Stand
        },
      }),
    );
  }

  #setPendingRead(uuid: string, isRead: boolean): void {
    this.#pendingReads.update(m => new Map(m).set(uuid, isRead));
  }

  #clearPendingRead(uuid: string): void {
    this.#pendingReads.update(m => {
      if (!m.has(uuid)) return m;
      const next = new Map(m);
      next.delete(uuid);
      return next;
    });
  }

  setIsRead(
    userNotificationUuid: string,
    isRead: boolean = true
  ): Observable<any> {

    if(this.#debug) console.warn('[NotificationsService] setIsRead, userNewsUuid=', userNotificationUuid, ', isRead=', isRead);

    const idOrKeyInput: IdOrKeyInput = {
      guess: Array.isArray(userNotificationUuid) ? userNotificationUuid[0] : userNotificationUuid,
    };

    if(this.#debug) console.warn('[NotificationsService] setIsRead, idOrKeyInput=', idOrKeyInput);

    return this.setIsReadGQL.mutate({
      input: idOrKeyInput,
      isRead: isRead as boolean
    }, { context: { client: 'sitosxapi' } }).pipe(
      take(1), // theoretisch nicht nötig, da mutate nur einmal emittiert
      map((result) => {
        if (this.#debug) console.warn('[NotificationsService] result=', result);
        return result;

        /*
        if (!result.data?.news?.setIsRead) {
          throw new Error(this.translateService.instant('news.NEWS_UPDATE_ERROR'));
        }
        
        return result.data.news.setIsRead;
        */
      }),
      catchError((error: unknown) => {
        // Fehler UNVERAENDERT weiterwerfen. Das vormalige `new Error(...)` behielt nur
        // die Message und verwarf die Apollo-Details (graphQLErrors/networkError).
        // Im Mutations-Pfad gibt es keinen DataService, der loggt -> die EINE Logzeile
        // bleibt hier.
        console.error('[NotificationsService] Failed to update notifications read status, error=', error);
        return throwError(() => error);
      })
    );

  }

  // ========================================================================
  // Private Methods
  // ========================================================================

  /**
   * Fetch nws from API
   */
  #fetchNotifications(): Observable<News[]> {
    // .fetch() statt .watch().valueChanges: einmalige Emission, kein langlebiger
    // QueryRef (kein Leak). fetchPolicy 'no-cache' -> weder Lesen noch Schreiben
    // am Apollo-Cache; garantiert der DB-Stand. take(1)/takeUntilDestroyed
    // übernimmt bereits DataService.load().
    return this.wigetNewsGQL
      .fetch({}, { context: { client: 'sitosxapi' }, fetchPolicy: 'no-cache' })
      .pipe(
        map(result => {
          const nodes = result.data.news?.newsForLoggedUser.nodes as News[] || [];
          // Defensive flache Kopie pro Node: garantiert neue, mutierbare Referenzen –
          // unabhaengig von fetchPolicy (falls kuenftig nicht mehr 'no-cache') und
          // davon, ob ein Consumer die Items nochmal mapped. Verhindert, dass ein
          // evtl. eingefrorenes/in-place aktualisiertes Apollo-Objekt durchgereicht
          // wird und dadurch Signal-/Change-Detection einen Inhaltswechsel verpasst.
          const notifications = nodes.map(n => ({ ...n }));
          if (this.#debug) console.log(`[NotificationsService] Loaded ${notifications.length} notifications, notifications=`, notifications);
          return notifications;
        }),
        // KEIN catchError hier: Fehler propagieren unveraendert (Default-Verhalten).
        // Der DataService setzt loading/error und loggt sie GENAU EINMAL; der
        // naechste Poll-Tick laedt unabhaengig neu (Polling stirbt NICHT).
      );
  }
}
